import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Task } from '../pages/job-summary/Interfaces';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentJob: any;
  onNewTaskAdded?: (newTask: Task) => void;
  editTask?: Task;
}

const NewTaskModal: React.FC<TaskModalProps> = ({ 
  isOpen, 
  onClose, 
  currentJob, 
  onNewTaskAdded,
  editTask 
}) => {
  const [showSuccessNotice, setShowSuccessNotice] = useState(false);

  const taskSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    task: Yup.string().required('Task is required'),
  });

  const formik = useFormik({
    initialValues: {
      title: editTask?.title || '',
      task: editTask?.task || '',
      jobLabel: currentJob ? currentJob.jobLabel : '',
    },
    validationSchema: taskSchema,
    onSubmit: async (values) => {
      try {
        if (editTask) {
          // Update existing task
          const taskRef = doc(db, 'tasks', editTask.id);
          const updateData = {
            title: values.title,
            task: values.task,
            lastEdited: new Date().toISOString(),
          };
          
          console.log('Updating task with ID:', editTask.id, 'with data:', updateData);
          await updateDoc(taskRef, updateData);
          
          // Create updated task object for the callback
          const updatedTask: Task = {
            ...editTask,
            title: values.title,
            task: values.task,
            lastEdited: new Date().toISOString(),
          };
          
          console.log('Task updated successfully');
          setShowSuccessNotice(true);
          
          // Call the callback with the updated task
          if (onNewTaskAdded) {
            onNewTaskAdded(updatedTask);
          }
          
          setTimeout(() => {
            setShowSuccessNotice(false);
            onClose();
            formik.resetForm();
          }, 1500);
        } else {
          // Create new task
          const docData = {
            id: `${Date.now()}`,
            timestamp: Date.now(),
            info: 'Task created',
            title: values.title,
            task: values.task,
            jobLabel: values.jobLabel,
            jobId: currentJob ? currentJob.id : null,
            lastEdited: new Date().toISOString(),
            feed: [
              {
                content: 'Task created',
                id: `${Date.now()}`,
                info: 'Task created',
                timestamp: Date.now(),
              },
            ],
          };

          await setDoc(doc(db, 'tasks', docData.id), docData);
          console.log('New task added successfully: ', docData);
          setShowSuccessNotice(true);
          
          if (onNewTaskAdded) {
            onNewTaskAdded(docData as Task);
          }
          
          setTimeout(() => {
            setShowSuccessNotice(false);
            onClose();
            formik.resetForm();
          }, 1500);
        }
      } catch (error) {
        console.error(editTask ? 'Error updating task: ' : 'Error adding new task: ', error);
      }
    },
  });

  useEffect(() => {
    if (editTask) {
      formik.setValues({
        title: editTask.title || '',
        task: editTask.task || '',
        jobLabel: currentJob ? currentJob.jobLabel : '',
      });
    }
  }, [editTask, currentJob]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal show={isOpen} onHide={onClose} centered style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <Modal.Header closeButton>
        <Modal.Title>{editTask ? 'Edit Task' : 'New Task'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showSuccessNotice && (
          <Alert variant="success">
            {editTask ? 'Task updated successfully!' : 'Task added successfully!'}
          </Alert>
        )}
        <Form onSubmit={formik.handleSubmit}>
          <Form.Group className="mb-3 d-flex align-items-center">
            <Form.Label className="me-3">
              <b>{currentJob ? currentJob.jobLabel : 'Job'}</b>
            </Form.Label>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              {...formik.getFieldProps('title')}
              isInvalid={formik.touched.title && !!formik.errors.title}
            />
            {formik.touched.title && formik.errors.title ? (
              <Form.Control.Feedback type="invalid">
                {formik.errors.title}
              </Form.Control.Feedback>
            ) : null}
            <Form.Control
              type="hidden"
              {...formik.getFieldProps('jobLabel')}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Task</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              {...formik.getFieldProps('task')}
              isInvalid={formik.touched.task && !!formik.errors.task}
            />
            {formik.touched.task && formik.errors.task ? (
              <Form.Control.Feedback type="invalid">
                {formik.errors.task}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Button variant="primary" type="submit">
            {editTask ? 'Update Task' : 'Save Changes'}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default NewTaskModal;